import React, { type ReactNode } from 'react';

import Box from '@atlaskit/primitives/box';
import { Grid } from '@atlaskit/primitives/grid';
import { media } from '@atlaskit/primitives/responsive/media';
import Stack from '@atlaskit/primitives/stack';
// eslint-disable-next-line @townsquare/rules/no-xcss
import { xcss } from '@atlaskit/primitives/xcss';
import { useRecommendedAgents } from '@atlassian/conversation-assistant-agent/controllers/agents';
import { RovoEntitlementSetter } from '@atlassian/conversation-assistant-entitlement/controllers/rovo-entitlement';
import type { Agent } from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { FormattedMessage, useIntl } from 'react-intl';

import { useAnalytics } from '@townsquare/analytics';
import { useRovoWorkspace } from '@townsquare/collab-context';
import { useOnMount } from '@townsquare/hooks';
import { teamsAppAgentsDirectoryRoute } from '@townsquare/routes/teams-app-agents-directory-view';
import { useRovoEntitlement } from '@townsquare/rovo/entitlement';
import { useAssistanceServiceParams } from '@townsquare/rovo/hooks';

import { Heading } from '../Heading';

import { Agent as AgentCard } from './Card';
import { AgentSkeleton } from './Card/Skeleton';

const MAX_AGENTS = 3;
const gridStyles = xcss({
	display: 'grid',
	gap: 'space.150',
	// eslint-disable-next-line @atlaskit/design-system/no-nested-styles
	[media.above.md]: { gap: 'space.200' },
	gridTemplateColumns: `repeat(auto-fill, minmax(352px, 1fr))`,
	padding: 'space.0',
});

const listItemStyles = xcss({
	display: 'contents',
});

function ConfigureRovoWrapper(): React.JSX.Element | null {
	const { data, loading } = useRovoEntitlement();

	if (loading) {
		return <AgentsRowSkeleton />;
	}

	if (data?.isRovoEnabled && data?.isRovoLLMEnabled) {
		return (
			<>
				<RovoEntitlementSetter isRovoEnabled={true} />
				<AgentsRowWithDataFetching />
			</>
		);
	}

	return null;
}

function BrowseAgentsView({
	children,
	onMount,
	useListMarkup,
}: {
	children: ReactNode;
	onMount?: () => void;
	useListMarkup?: boolean;
}) {
	const intl = useIntl();

	useOnMount(() => {
		if (onMount) onMount();
	});

	return (
		<Stack space="space.100" testId="browse-agents-section">
			<Heading
				browseMore={{
					text: intl.formatMessage({
						id: 'townsquare.web.staff-directory-view.browse-all-agents',
						defaultMessage: 'Browse all agents',
						description:
							'Link text in the recommended agents section heading that navigates to the full agents directory.',
					}),
					linkProps: {
						to: teamsAppAgentsDirectoryRoute,
						query: {
							screen: 'SEARCH_AGENTS',
						},
					},
				}}
				screenType="BROWSE_AGENTS"
			>
				<FormattedMessage
					id="townsquare.staff-directory.browse-agents-section.title"
					description="Title for the recommended agents section"
					defaultMessage="Recommended agents"
				/>
			</Heading>
			<Grid xcss={gridStyles} as={useListMarkup ? 'ul' : undefined}>
				{children}
			</Grid>
		</Stack>
	);
}

export function AgentsRowSkeleton(): React.JSX.Element {
	return (
		<BrowseAgentsView>
			{Array.from({ length: MAX_AGENTS }).map((_, index) => (
				<AgentSkeleton key={index} />
			))}
		</BrowseAgentsView>
	);
}

function AgentsRowWithDataFetching() {
	const rovoWorkspaceCloudId = useRovoWorkspace();
	const cloudId = rovoWorkspaceCloudId ?? '';
	const assistanceServiceParams = useAssistanceServiceParams({
		experienceId: 'teams-agents-for-you',
		touchpointSource: 'teams-agents-for-you',
	});
	const { isFetching, recommendedAgents } = useRecommendedAgents({ assistanceServiceParams });

	if (isFetching) {
		return <AgentsRowSkeleton />;
	}

	if (!recommendedAgents || recommendedAgents.length < MAX_AGENTS) {
		return null;
	}

	return <AgentsRowUI agents={recommendedAgents.slice(0, MAX_AGENTS)} cloudId={cloudId} />;
}

export function AgentsRowUI({
	agents,
	cloudId,
}: {
	agents: Agent[];
	cloudId: string;
}): React.JSX.Element {
	const analytics = useAnalytics();

	return (
		<BrowseAgentsView
			onMount={() => void analytics.screen('recommendedAgentsSection')}
			useListMarkup
		>
			{agents.map((agent) => (
				<Box as="li" key={agent.id} xcss={listItemStyles}>
					<AgentCard {...agent} siteId={cloudId} />
				</Box>
			))}
		</BrowseAgentsView>
	);
}

// Single re-export only, not a barrel.
// eslint-disable-next-line no-barrel-files/no-barrel-files
export { ConfigureRovoWrapper as AgentsRow };
